'use client'

import { useContext, useEffect, useRef } from 'react'

import _startCase from 'lodash/startCase'
import { useParams, usePathname } from 'next/navigation'
import { useShallow } from 'zustand/react/shallow'

import { routes } from '@constants/routes'
import { usePrevious } from '@hooks/utils'
import { SeoParams } from '@page/seo-location-space-type-use-case/types'
import { AnalyticsContext } from '@services/Analytics/context'
import { useSearchStore } from '@state/use-search-store'

export function useOnPathChange() {
  const { page } = useContext(AnalyticsContext)

  const pathname = usePathname()
  const params = useParams<SeoParams>()

  const previousPathname = usePrevious(pathname)
  const initial = useRef(true)

  const { clearSearchFilters } = useSearchStore(
    useShallow((state) => ({
      clearSearchFilters: state.clearSearchFilters,
    }))
  )

  useEffect(() => {
    const name = pathname.replace('/', '') // remove first forward slash

    // When we navigate away from search, clear some filters
    if (
      pathname !== previousPathname &&
      previousPathname?.startsWith(routes.Search())
    ) {
      clearSearchFilters()
    }

    if (pathname === previousPathname && !initial.current) return

    initial.current = false

    const isSeoGeneratedPage =
      !!pathname.includes('/explore/') &&
      !!params.location &&
      !!params.spaceTypeUseCase
    let seoPageCity, seoPageRegion, seoPageCountry, seoPageTopic
    if (isSeoGeneratedPage) {
      seoPageTopic = params.spaceTypeUseCase
      const cityParts = params.location.split('-')
      if (cityParts.length >= 3) {
        const [country, region, ...city] = cityParts
        seoPageCity = _startCase(city.join(' '))
        seoPageRegion = region.toUpperCase()
        seoPageCountry = country.toUpperCase()
      }
    }

    page?.(name, {
      title: document.title,

      path: pathname,
      url: window?.location?.href,
      search: window?.location?.search,

      isSeoGeneratedPage,
      seoPageCity,
      seoPageRegion,
      seoPageCountry,
      seoPageTopic,
    })
  }, [
    page,
    clearSearchFilters,
    params.location,
    params.spaceTypeUseCase,
    pathname,
    previousPathname,
  ])
}
